import { useEffect } from 'react';

import {
	useAnnotationManager,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { expValEquals } from '@confluence/feature-experiments';

export function usePreemptiveGate({ onConfirm: handleConfirm }: { onConfirm: () => void }): void {
	const annotationManager = useAnnotationManager();
	const { setEditorCommentCloseGuard } = useAnnotationsDispatch();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();

	// Register the shared guard that prevents comment navigation or closing from discarding
	// unsaved content without confirmation.
	useEffect(() => {
		if (
			!annotationManager &&
			(!expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) ||
				!expValEquals('confluence_suggested_edits_inline_comment_fix', 'isEnabled', true))
		) {
			return;
		}

		const closeGuard = !hasContentChanged
			? () => Promise.resolve(true)
			: () =>
					new Promise<boolean>((resolve) => {
						showModal(CommentWarningDialog, {
							onConfirm: () => {
								resetContentChanged();
								handleConfirm();
								resolve(true);
							},
							onCancel: () => resolve(false),
							onClose: () => resolve(false),
						});
					});

		annotationManager?.setPreemptiveGate(closeGuard);
		if (
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
			expValEquals('confluence_suggested_edits_inline_comment_fix', 'isEnabled', true)
		) {
			setEditorCommentCloseGuard(closeGuard);
		}

		return () => {
			const allowClose = () => Promise.resolve(true);
			annotationManager?.setPreemptiveGate(allowClose);
			if (
				expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
				expValEquals('confluence_suggested_edits_inline_comment_fix', 'isEnabled', true)
			) {
				setEditorCommentCloseGuard(allowClose);
			}
		};
	}, [
		hasContentChanged,
		annotationManager,
		showModal,
		resetContentChanged,
		handleConfirm,
		setEditorCommentCloseGuard,
	]);
}
